import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  Pencil,
  ExternalLink,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Github,
  Linkedin,
  FileText,
  MessageSquare,
} from "lucide-react";
import type { LateralCandidate, LateralCVEvaluationResult } from "@/types/database";
import {
  LATERAL_STATUS_OPTIONS as STATUS_OPTIONS,
  LATERAL_STATUS_COLORS as STATUS_COLORS,
} from "@/lib/lateral-statuses";

function scoreColor(score: number) {
  if (score >= 70) return "text-green-600";
  if (score >= 50) return "text-yellow-600";
  return "text-red-600";
}

function InfoRow({ label, value }: { label: string; value?: string | number | null }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-gray-500 text-xs">{label}</p>
      <p className="font-medium text-sm">{value}</p>
    </div>
  );
}

function AnswerBlock({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded p-3 border">{value}</p>
    </div>
  );
}

export default function LateralCandidateEvaluationPage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isRevaluating, setIsRevaluating] = useState(false);
  const [statusDraft, setStatusDraft] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);

  const { data: candidate, isLoading } = useQuery({
    queryKey: ["lateral-candidate", candidateId],
    queryFn: async () => {
      if (!candidateId) throw new Error("Candidate ID required");
      const { data, error } = await supabase
        .from("lateral_candidates")
        .select("*, lateral_job_positions(job_title, job_description, ai_evaluation_prompt)")
        .eq("id", candidateId)
        .single();
      if (error) throw error;
      return data as LateralCandidate & {
        lateral_job_positions?: {
          job_title: string;
          job_description: string | null;
          ai_evaluation_prompt: string | null;
        } | null;
      };
    },
    enabled: !!candidateId,
  });

  const saveStatus = useMutation({
    mutationFn: async (status: string) => {
      const { error } = await supabase
        .from("lateral_candidates")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", candidateId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lateral-candidate", candidateId] });
      queryClient.invalidateQueries({ queryKey: ["lateral-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["lateral-kanban-col"] });
      toast({ title: "Status updated" });
    },
    onError: (e: Error) =>
      toast({ title: "Failed to update status", description: e.message, variant: "destructive" }),
  });

  const saveNotes = useMutation({
    mutationFn: async (notes: string) => {
      const { error } = await supabase
        .from("lateral_candidates")
        .update({ notes, updated_at: new Date().toISOString() })
        .eq("id", candidateId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lateral-candidate", candidateId] });
      setNotesDraft(null);
      toast({ title: "Notes saved" });
    },
    onError: (e: Error) =>
      toast({ title: "Failed to save notes", description: e.message, variant: "destructive" }),
  });

  const handleRevaluate = async () => {
    if (!candidate?.resume_markdown) {
      toast({
        title: "No resume content",
        description: "This candidate has no parsed resume to evaluate.",
        variant: "destructive",
      });
      return;
    }
    setIsRevaluating(true);
    try {
      const position = (candidate as any).lateral_job_positions;
      const result = await invokeEdgeFunction<LateralCVEvaluationResult>("evaluate-lateral-cv", {
        body: {
          resumeMarkdown: candidate.resume_markdown,
          jobTitle: position?.job_title ?? "",
          jobDescription: position?.job_description ?? "",
          aiEvaluationPrompt: position?.ai_evaluation_prompt ?? "",
          expectedCTC: candidate.expected_ctc ?? "",
          currentCTC: candidate.current_ctc ?? "",
          inHandSalary: candidate.in_hand_salary ?? "",
          currentLocation: candidate.current_location ?? "",
          totalCompanies: candidate.total_companies ?? "",
          avgTenurePerCompany: candidate.avg_tenure_per_company ?? "",
          yearsOfExperience:
            candidate.total_experience_years != null
              ? String(candidate.total_experience_years)
              : "",
        },
      });

      const score = Math.max(0, Math.min(100, Math.round(result.score)));
      const { error } = await supabase
        .from("lateral_candidates")
        .update({
          cv_score: score,
          cv_evaluation_result: result,
          updated_at: new Date().toISOString(),
        })
        .eq("id", candidateId!);
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["lateral-candidate", candidateId] });
      toast({ title: "CV re-evaluated", description: `Score: ${score}/100` });
    } catch (err: any) {
      toast({ title: "Evaluation failed", description: err.message, variant: "destructive" });
    } finally {
      setIsRevaluating(false);
    }
  };

  if (isLoading) return <div className="text-center py-8 text-gray-500">Loading...</div>;
  if (!candidate) return <div className="text-center py-8 text-gray-500">Candidate not found</div>;

  const evalResult = candidate.cv_evaluation_result as LateralCVEvaluationResult | null;
  const currentStatus = statusDraft ?? candidate.status;
  const currentNotes = notesDraft ?? (candidate.notes ?? "");
  const position = (candidate as any).lateral_job_positions;

  const hasApplicationAnswers =
    candidate.why_hire_you ||
    candidate.reason_for_job_change ||
    candidate.why_join_us ||
    candidate.top_5_projects ||
    candidate.college_name ||
    candidate.highest_qualification ||
    candidate.interview_preferred_date;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{candidate.full_name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className={STATUS_COLORS[candidate.status] ?? "bg-gray-100 text-gray-700"}>
                {STATUS_OPTIONS.find((s) => s.value === candidate.status)?.label ?? candidate.status}
              </Badge>
              {position?.job_title && (
                <span className="text-xs text-gray-500">{position.job_title}</span>
              )}
              {candidate.cv_score != null && (
                <span className={`text-sm font-semibold ${scoreColor(candidate.cv_score)}`}>
                  CV Score: {candidate.cv_score}/100
                </span>
              )}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/lateral/candidates/${candidateId}/edit`)}
          className="shrink-0"
        >
          <Pencil className="h-4 w-4 mr-1.5" />
          Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left — Profile + Application + Resume tabs */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base">Candidate Information</CardTitle>
              {candidate.resume_url && (
                <a
                  href={candidate.resume_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                  <FileText className="h-3.5 w-3.5" />
                  View Resume PDF
                </a>
              )}
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="profile">
                <TabsList className={`grid w-full ${hasApplicationAnswers ? "grid-cols-3" : "grid-cols-2"}`}>
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  {hasApplicationAnswers && (
                    <TabsTrigger value="application">
                      <MessageSquare className="h-3.5 w-3.5 mr-1" />
                      Application
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="resume">Resume</TabsTrigger>
                </TabsList>

                {/* Profile tab */}
                <TabsContent value="profile" className="space-y-4 mt-4">
                  {(candidate.current_designation || candidate.current_company) && (
                    <div>
                      <p className="text-gray-500 text-xs">Current Role</p>
                      <p className="font-medium text-sm">
                        {[candidate.current_designation, candidate.current_company]
                          .filter(Boolean)
                          .join(" @ ")}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <InfoRow label="Email" value={candidate.email} />
                    <InfoRow label="Phone" value={candidate.phone} />
                    <InfoRow label="WhatsApp" value={candidate.whatsapp_number} />
                    <InfoRow label="Current Location" value={candidate.current_location} />
                    <InfoRow label="Total Experience" value={candidate.total_experience_years != null ? `${candidate.total_experience_years} yrs` : null} />
                    <InfoRow label="Relevant Experience" value={candidate.relevant_experience_years != null ? `${candidate.relevant_experience_years} yrs` : null} />
                    <InfoRow label="Total Companies" value={candidate.total_companies} />
                    <InfoRow label="Avg Tenure (months)" value={candidate.avg_tenure_per_company != null ? String(candidate.avg_tenure_per_company) : null} />
                    <InfoRow label="Current CTC" value={candidate.current_ctc} />
                    <InfoRow label="In-Hand Salary" value={candidate.in_hand_salary} />
                    <InfoRow label="Expected CTC" value={candidate.expected_ctc} />
                    <InfoRow label="Notice Period" value={candidate.notice_period} />
                  </div>

                  {candidate.permanent_address && (
                    <div>
                      <p className="text-gray-500 text-xs">Permanent Address</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{candidate.permanent_address}</p>
                    </div>
                  )}

                  {candidate.key_skills && candidate.key_skills.length > 0 && (
                    <div>
                      <p className="text-gray-500 text-xs mb-1.5">Key Skills</p>
                      <div className="flex flex-wrap gap-1">
                        {candidate.key_skills.map((s) => (
                          <span key={s} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {candidate.technologies && candidate.technologies.length > 0 && (
                    <div>
                      <p className="text-gray-500 text-xs mb-1.5">Technologies</p>
                      <div className="flex flex-wrap gap-1">
                        {candidate.technologies.map((t) => (
                          <span key={t} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-1">
                    {candidate.linkedin_url && (
                      <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                        <Linkedin className="h-3 w-3" />
                        LinkedIn
                      </a>
                    )}
                    {candidate.github_url && (
                      <a href={candidate.github_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-gray-600 hover:underline">
                        <Github className="h-3 w-3" />
                        GitHub
                      </a>
                    )}
                  </div>
                </TabsContent>

                {/* Application answers tab */}
                {hasApplicationAnswers && (
                  <TabsContent value="application" className="space-y-4 mt-4">
                    <AnswerBlock label="Why should we hire you?" value={candidate.why_hire_you} />
                    <AnswerBlock label="Reason for job change" value={candidate.reason_for_job_change} />
                    <AnswerBlock label="Why join us?" value={candidate.why_join_us} />
                    <AnswerBlock label="Top 5 Projects" value={candidate.top_5_projects} />

                    {(candidate.college_name || candidate.highest_qualification) && (
                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <InfoRow label="College / University" value={candidate.college_name} />
                        <InfoRow label="Highest Qualification" value={candidate.highest_qualification} />
                      </div>
                    )}

                    {(candidate.interview_preferred_date || candidate.interview_preferred_time) && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Interview Preference
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <InfoRow label="Preferred Date" value={candidate.interview_preferred_date} />
                          <InfoRow label="Preferred Time" value={candidate.interview_preferred_time} />
                        </div>
                      </div>
                    )}
                  </TabsContent>
                )}

                {/* Resume markdown tab */}
                <TabsContent value="resume" className="mt-4">
                  {candidate.resume_markdown ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none bg-gray-50 p-4 rounded-lg border max-h-[500px] overflow-y-auto">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {candidate.resume_markdown}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      No parsed resume content available.
                      {candidate.resume_url && (
                        <span>
                          {" "}
                          <a href={candidate.resume_url} target="_blank" rel="noopener noreferrer"
                            className="text-blue-600 hover:underline">
                            View original PDF
                          </a>
                        </span>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={currentNotes}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Add recruiter notes..."
                rows={4}
                className="text-sm resize-none"
              />
              {notesDraft !== null && notesDraft !== (candidate.notes ?? "") && (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setNotesDraft(null)}>
                    Discard
                  </Button>
                  <Button size="sm" onClick={() => saveNotes.mutate(notesDraft)} disabled={saveNotes.isPending}>
                    {saveNotes.isPending ? "Saving..." : "Save Notes"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right — Evaluation + Status */}
        <div className="space-y-4">
          {/* CV Evaluation */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">CV Evaluation</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRevaluate}
                disabled={isRevaluating || !candidate.resume_markdown}
                title={!candidate.resume_markdown ? "No parsed resume available" : undefined}
              >
                {isRevaluating ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                )}
                {isRevaluating ? "Evaluating..." : "Re-evaluate"}
              </Button>
            </CardHeader>
            <CardContent>
              {!evalResult ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No evaluation yet.{" "}
                  {candidate.resume_markdown
                    ? "Click Re-evaluate to run AI evaluation."
                    : "Upload a resume first to enable evaluation."}
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="text-center">
                    <span className={`text-4xl font-bold ${scoreColor(evalResult.score)}`}>
                      {evalResult.score}
                    </span>
                    <span className="text-lg text-gray-400">/100</span>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {evalResult.pros && evalResult.pros.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
                          Strengths
                        </p>
                        <ul className="space-y-1">
                          {evalResult.pros.map((pro, i) => (
                            <li key={i} className="flex gap-2 text-sm text-gray-700">
                              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {evalResult.cons && evalResult.cons.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">
                          Gaps
                        </p>
                        <ul className="space-y-1">
                          {evalResult.cons.map((con, i) => (
                            <li key={i} className="flex gap-2 text-sm text-gray-700">
                              <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {evalResult.considerations && (
                      <div>
                        <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-2">
                          If Proceeding
                        </p>
                        <div className="flex gap-2">
                          <AlertCircle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                          <p className="text-sm text-gray-700">{evalResult.considerations}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status Update */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Update Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={currentStatus} onValueChange={(v) => setStatusDraft(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {statusDraft !== null && statusDraft !== candidate.status && (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setStatusDraft(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      saveStatus.mutate(statusDraft);
                      setStatusDraft(null);
                    }}
                    disabled={saveStatus.isPending}
                  >
                    {saveStatus.isPending ? "Saving..." : "Save Status"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
